import type {LocationValue} from '@/types/models/creator-crm/creatorCrm';
import {
  AudienceAgeSelection,
  BaseFilterOption,
  DynamicPlatformFilters,
  Filter,
  FilterOptionCompare,
  MultiSelection,
  MultiSelectionAsync,
  RangeSelection,
  SingleSelection,
  WeightedFilterOption,
  WeightedSingleSelection,
} from '@/types/models/creator-crm/filter';
import {PlatformFilterIds} from '@/types/models/creator-crm/filterId';
import locationFetchers from '@/api/fetchers/location';
import {
  DEFAULT_AGE_STEPS,
  DEFAULT_AVERAGE_VIEWS_STEPS,
  DEFAULT_FOLLOWERS_STEPS,
  DEFAULT_WEIGHTS_OPTIONS,
} from '@/stores/creator-crm/constants';
import {
  defaultAgeFilterOptions,
  defaultGenderFilterOptions,
  defaultPlatformFilterOptions,
  defaultVettingDecisionFilterOptions,
  defaultCampaignHistoryFilterOptions,
  engagementRateFilterOptions,
} from '@/stores/creator-crm/defaultFiltersOptions';

function eqStr(a?: string, b?: string): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.toLowerCase().trim() === b.toLowerCase().trim();
}

function isSameLocation(
  a: Partial<LocationValue>,
  b: Partial<LocationValue>
): boolean {
  const bothHaveCoords =
    Number.isFinite(a.longitude) &&
    Number.isFinite(a.latitude) &&
    Number.isFinite(b.longitude) &&
    Number.isFinite(b.latitude);
  if (
    bothHaveCoords &&
    (a.longitude !== b.longitude || a.latitude !== b.latitude)
  ) {
    return false;
  }
  return (
    eqStr(a.place, b.place) &&
    eqStr(a.region, b.region) &&
    eqStr(a.country, b.country)
  );
}

function areLocationOptionsEqual(
  a: FilterOptionCompare,
  b: FilterOptionCompare
): boolean {
  if (a.id === b.id) return true;
  return isSameLocation(
    (a.value ?? {}) as Partial<LocationValue>,
    (b.value ?? {}) as Partial<LocationValue>
  );
}

export const genderFilter: SingleSelection<BaseFilterOption> = {
  type: 'singleSelection',
  presentationType: 'singleSelectionOptionsSegmentedControl',
  localeLabelKey: 'Gender',
  options: defaultGenderFilterOptions,
  selected: defaultGenderFilterOptions[0],
};

export const ageFilter: RangeSelection = {
  type: 'rangeSelection',
  presentationType: 'rangeSelectionSliderAndInput',
  localeLabelKey: 'Age',
  steps: DEFAULT_AGE_STEPS,
};

export const locationFilter: MultiSelectionAsync<BaseFilterOption> = {
  type: 'multiSelectionAsync',
  presentationType: 'multiSelectionOptionsAutocompleteAsync',
  localeLabelKey: 'Location',
  getOptions: locationFetchers.locations,
  selected: [],
  placeholderLocaleLabelKey: 'Search location...',
  isOptionEqualToValue: areLocationOptionsEqual,
};

export const categoryFilter: MultiSelection<BaseFilterOption> = {
  type: 'multiSelection',
  presentationType: 'multiSelectionOptionsList',
  localeLabelKey: 'Category',
  options: [],
  selected: [],
  asyncInitializationKey: 'categories',
};

export const vettingDecisionFilter: MultiSelection<BaseFilterOption> = {
  type: 'multiSelection',
  presentationType: 'multiSelectionOptionsList',
  localeLabelKey: 'Safe Collab Status',
  options: defaultVettingDecisionFilterOptions,
  selected: [],
};

export const campaignHistoryFilter: SingleSelection<BaseFilterOption> = {
  type: 'singleSelection',
  presentationType: 'singleSelectionOptionsRadioButtonsListWithIcon',
  localeLabelKey: 'Campaign History',
  options: defaultCampaignHistoryFilterOptions,
};

export const platformFilter: SingleSelection<BaseFilterOption> = {
  type: 'singleSelection',
  presentationType: 'singleSelectionOptionsRadioButtonsListWithIcon',
  localeLabelKey: 'Platform',
  tooltipLocaleLabelKey: 'Select a platform to filter creators',
  options: defaultPlatformFilterOptions,
  selected: defaultPlatformFilterOptions[0],
};

export const weightedGenderFilter: WeightedSingleSelection<WeightedFilterOption> =
  {
    ...genderFilter,
    options: defaultGenderFilterOptions.filter(
      (gender) => gender.id !== 'other'
    ),
    localeLabelKey: 'Audience Gender',
    type: 'weightedSingleSelection',
    presentationType: 'weightedSingleSelectionOptionsSegmentedControl',
    weights: DEFAULT_WEIGHTS_OPTIONS,
    weightPlaceholderLocaleLabelKey: 'Weight',
    isIrrelevant: true,
  };

export const audienceAgeFilter: AudienceAgeSelection = {
  localeLabelKey: 'Audience Age',
  type: 'audienceAge',
  presentationType: 'audienceAge',
  options: defaultAgeFilterOptions,
  selected: [],
  ldaSelected: false,
  ldaLocaleLabelKey: 'LDA',
  weights: DEFAULT_WEIGHTS_OPTIONS,
  placeholderLocaleLabelKey: 'Select age range',
  weightPlaceholderLocaleLabelKey: '%',
  isIrrelevant: true,
};

export const audienceLocationFilter: MultiSelectionAsync<WeightedFilterOption> =
  {
    ...locationFilter,
    localeLabelKey: 'Audience Location',
    isIrrelevant: true,
    getOptions: (query) => locationFetchers.locations(query, true, true),
  };

export const followersFilter: RangeSelection = {
  type: 'rangeSelection',
  presentationType: 'rangeSelectionSliderAndInput',
  localeLabelKey: 'Followers',
  steps: DEFAULT_FOLLOWERS_STEPS,
};

export const subscribersFilter: RangeSelection = {
  ...followersFilter,
  localeLabelKey: 'Subscribers',
};

export const averageViewsFilter: RangeSelection = {
  type: 'rangeSelection',
  presentationType: 'rangeSelectionSliderAndInput',
  localeLabelKey: 'Average views',
  steps: DEFAULT_AVERAGE_VIEWS_STEPS,
};

export const engagementRateFilter: SingleSelection<BaseFilterOption> = {
  type: 'singleSelection',
  presentationType: 'singleSelectionOptionsDropDown',
  localeLabelKey: 'Eng Rate',
  options: engagementRateFilterOptions,
  placeholderLocaleLabelKey: 'Select',
};

export const averageEngagementRateFilter: SingleSelection<BaseFilterOption> = {
  ...engagementRateFilter,
  localeLabelKey: 'Average Engagement',
};

const basePlatformFilters: Partial<Record<PlatformFilterIds, Filter>> = {
  followers: followersFilter,
  engagementRate: engagementRateFilter,
};

export const platformMetricsFilter: DynamicPlatformFilters = {
  type: 'dynamicPlatformFilters',
  presentationType: 'platformFiltersGroup',
  localeLabelKey: 'Platform Metrics',
  platformId: 'all',
  platformsFilters: {
    instagram: {
      ...basePlatformFilters,
    },
    tiktok: {
      ...basePlatformFilters,
      averageViews: averageViewsFilter,
    },
    twitter: {...basePlatformFilters},
    youtube: {
      followers: subscribersFilter,
      engagementRate: averageEngagementRateFilter,
      averageViews: averageViewsFilter,
    },
    facebook: {...basePlatformFilters},
    pinterest: {...basePlatformFilters},
    all: {...basePlatformFilters},
  },
};
